<!-- Page Header -->
<div class="mb-4">
  <h1 class="h2 mb-1 fw-bold">Certifications</h1>
  <p class="text-muted mb-0">Manage your organization and personal certifications</p>
</div>

<!-- Tabs -->
<ul class="nav nav-tabs mb-3">
  <li class="nav-item">
    <button
      class="nav-link"
      [class.active]="activeTab === 'organization'"
      (click)="setActiveTab('organization')"
    >
      <i class="bi bi-building me-2"></i>
      Organization
      <span
        *ngIf="missingOrgCertsCount > 0"
        class="badge bg-primary ms-2"
      >
        {{ missingOrgCertsCount }}
      </span>
    </button>
  </li>
  <li class="nav-item">
    <button
      class="nav-link"
      [class.active]="activeTab === 'user'"
      (click)="setActiveTab('user')"
    >
      <i class="bi bi-person me-2"></i>
      Personal
      <span
        *ngIf="missingUserCertsCount > 0"
        class="badge bg-primary ms-2"
      >
        {{ missingUserCertsCount }}
      </span>
    </button>
  </li>
</ul>

<!-- Organization Certifications Tab -->
<div *ngIf="activeTab === 'organization'">
  <!-- Info Banner -->
  <div class="alert alert-info d-flex align-items-start mb-3 py-2 px-3">
    <i class="bi bi-info-circle me-2 mt-1"></i>
    <div>
      <span class="small">
        Organization certifications belong to <strong>{{ organizationName }}</strong>.
        All certifications below are required before pilots can operate.
      </span>
    </div>
  </div>

  <!-- Uploaded Certifications -->
  <div class="card" [class.mb-3]="missingOrgCertTypes.length > 0 || optionalOrgCertTypes.length > 0">
    <div class="card-header p-3">
      <h5 class="mb-0">Your Certifications</h5>
    </div>
    <div class="card-body p-0">
      <ng-container *ngIf="uploadedOrgCertTypes.length > 0; else noOrgCerts">
        <ng-container *ngFor="let certType of uploadedOrgCertTypes; let last = last">

          <!-- Single-cert type (default) -->
          <ng-container *ngIf="!allowsMultiple(certType)">
            <div
              class="cert-item cert-uploaded p-3"
              [class.border-bottom]="!last"
            >
              <div class="d-flex justify-content-between align-items-start">
                <div class="d-flex align-items-start gap-3 flex-grow-1">
                  <div class="cert-icon flex-shrink-0">
                    <i class="bi bi-check-circle cert-icon-checked"></i>
                  </div>
                  <div class="flex-grow-1">
                    <div class="d-flex align-items-center gap-2 mb-1">
                      <h6 class="mb-0 fw-medium cert-title">{{ certType.label }}</h6>
                      <span *ngIf="certType.isRequired" class="badge badge-required small">Required</span>
                    </div>
                    <p class="small mb-0 cert-description text-muted">{{ certType.description }}</p>

                    <!-- Certification details -->
                    <div *ngIf="getCertificationForType(certType) as cert" class="mt-2 d-flex flex-wrap gap-3">
                      <div>
                        <span class="text-muted small">Certificate #:</span>
                        <span class="small fw-medium ms-1">{{ cert.certificateNumber }}</span>
                      </div>
                      <div *ngIf="cert.expirationDate">
                        <span class="text-muted small">Expires:</span>
                        <span class="small fw-medium ms-1">{{ formatDate(cert.expirationDate) }}</span>
                      </div>
                      <span class="badge" [ngClass]="getStatusBadgeClass(cert)">
                        {{ getStatusText(cert) }}
                      </span>
                    </div>
                  </div>
                </div>

                <div class="flex-shrink-0 ms-3">
                  <ng-container *ngIf="getCertificationForType(certType) as cert">
                    <div class="btn-group btn-group-sm">
                      @if(canEditOrganization){
                        <button class="btn btn-outline-secondary" (click)="editCertification(cert)">
                          <i class="bi bi-pencil"></i>
                        </button>
                      }
                      <button class="btn btn-outline-secondary" (click)="viewDocument(cert)" *ngIf="cert.documentUrl">
                        <i class="bi bi-file-earmark"></i>
                      </button>
                    </div>
                  </ng-container>
                </div>
              </div>
            </div>
          </ng-container>

          <!-- Multi-cert type (state-applicator) -->
          <ng-container *ngIf="allowsMultiple(certType)">
            <div
              *ngFor="let cert of getCertificationsForType(certType)"
              class="cert-item cert-uploaded p-3 border-bottom"
            >
              <div class="d-flex justify-content-between align-items-start">
                <div class="d-flex align-items-start gap-3 flex-grow-1">
                  <div class="cert-icon flex-shrink-0">
                    <i class="bi bi-check-circle cert-icon-checked"></i>
                  </div>
                  <div class="flex-grow-1">
                    <div class="d-flex align-items-center gap-2 mb-1">
                      <h6 class="mb-0 fw-medium cert-title">{{ certType.label }}{{ cert.state ? ' (' + cert.state + ')' : '' }}</h6>
                      <span *ngIf="certType.isRequired" class="badge badge-required small">Required</span>
                    </div>
                    <p class="small mb-0 cert-description text-muted">{{ certType.description }}</p>

                    <div class="mt-2 d-flex flex-wrap gap-3">
                      <div>
                        <span class="text-muted small">Certificate #:</span>
                        <span class="small fw-medium ms-1">{{ cert.certificateNumber }}</span>
                      </div>
                      <div *ngIf="cert.expirationDate">
                        <span class="text-muted small">Expires:</span>
                        <span class="small fw-medium ms-1">{{ formatDate(cert.expirationDate) }}</span>
                      </div>
                      <span class="badge" [ngClass]="getStatusBadgeClass(cert)">
                        {{ getStatusText(cert) }}
                      </span>
                    </div>
                  </div>
                </div>

                <div class="flex-shrink-0 ms-3">
                  <div class="btn-group btn-group-sm">
                    @if (canEditOrganization) {
                      <button class="btn btn-outline-secondary" (click)="editCertification(cert)">
                        <i class="bi bi-pencil"></i>
                      </button>
                    }
                    <button class="btn btn-outline-secondary" (click)="viewDocument(cert)" *ngIf="cert.documentUrl">
                      <i class="bi bi-file-earmark"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            @if(canEditOrganization){
              <!-- Add for Another Cert Type -->
              <div class="cert-item p-3" [class.border-bottom]="!last">
                <div class="d-flex align-items-center gap-3 ps-1">
                  <div class="cert-icon flex-shrink-0">
                    <i class="bi bi-plus-circle text-primary"></i>
                  </div>
                  <button class="btn btn-sm btn-outline-primary" (click)="addCertification(certType)">
                    <i class="bi bi-plus me-1"></i>{{ addCertificationText(certType) }}
                  </button>
                </div>
              </div>
            }
          </ng-container>

        </ng-container>
      </ng-container>
      <ng-template #noOrgCerts>
        <div class="p-3 text-center text-muted">
          <i class="bi bi-folder2-open me-2"></i>
          No certifications uploaded yet
        </div>
      </ng-template>
    </div>
  </div>

  <!-- Missing Required Certifications -->
  <div class="card" [class.mb-3]="optionalOrgCertTypes.length > 0" *ngIf="missingOrgCertTypes.length > 0">
    <div class="card-header p-3">
      <h5 class="mb-0">Required Certifications</h5>
    </div>
    <div class="card-body p-0">
      <ng-container *ngFor="let certType of missingOrgCertTypes; let last = last">
        <div
          class="cert-item cert-placeholder p-3"
          [class.border-bottom]="!last"
        >
          <div class="d-flex justify-content-between align-items-start">
            <div class="d-flex align-items-start gap-3 flex-grow-1">
              <div class="cert-icon flex-shrink-0">
                <i class="bi bi-circle text-muted"></i>
              </div>
              <div class="flex-grow-1">
                <div class="d-flex align-items-center gap-2 mb-1">
                  <h6 class="mb-0 fw-medium cert-title">{{ certType.label }}</h6>
                  <span class="badge badge-required small">Required</span>
                  <span class="badge badge-not-uploaded small">Not Uploaded</span>
                </div>
                <p class="small mb-0 cert-description">{{ certType.description }}</p>
              </div>
            </div>

            @if(canEditOrganization){
              <div class="flex-shrink-0 ms-3">
                <button class="btn btn-sm btn-primary" (click)="addCertification(certType)">
                  <i class="bi bi-plus me-1"></i>Add
                </button>
              </div>
            }
          </div>
        </div>
      </ng-container>
    </div>
  </div>

  <!-- Optional Certifications -->
  <div class="card" *ngIf="optionalOrgCertTypes.length > 0">
    <div class="card-header p-3">
      <h5 class="mb-0">Optional Certifications</h5>
    </div>
    <div class="card-body p-0">
      <ng-container *ngFor="let certType of optionalOrgCertTypes; let last = last">
        <div
          class="cert-item cert-placeholder p-3"
          [class.border-bottom]="!last"
        >
          <div class="d-flex justify-content-between align-items-start">
            <div class="d-flex align-items-start gap-3 flex-grow-1">
              <div class="cert-icon flex-shrink-0">
                <i class="bi bi-circle text-muted"></i>
              </div>
              <div class="flex-grow-1">
                <div class="d-flex align-items-center gap-2 mb-1">
                  <h6 class="mb-0 fw-medium cert-title">{{ certType.label }}</h6>
                </div>
                <p class="small mb-0 cert-description">{{ certType.description }}</p>
              </div>
            </div>

            @if(canEditOrganization){
              <div class="flex-shrink-0 ms-3">
                <button class="btn btn-sm btn-outline-primary" (click)="addCertification(certType)">
                  <i class="bi bi-plus me-1"></i>Add
                </button>
              </div>
            }
          </div>
        </div>
      </ng-container>
    </div>
  </div>
</div>

<!-- User Certifications Tab -->
<div *ngIf="activeTab === 'user'">
  <!-- Info Banner -->
  <div class="alert alert-info d-flex align-items-start mb-3 py-2 px-3">
    <i class="bi bi-info-circle me-2 mt-1"></i>
    <div>
      <span class="small">
        Personal certifications belong to you and apply to all organizations you're a member of.
        Part 107 and State Applicator License are required to operate drones.
      </span>
    </div>
  </div>

  <!-- Uploaded Certifications -->
  <div class="card" [class.mb-3]="missingUserCertTypes.length > 0 || optionalUserCertTypes.length > 0">
    <div class="card-header p-3">
      <h5 class="mb-0">Your Certifications</h5>
    </div>
    <div class="card-body p-0">
      <ng-container *ngIf="uploadedUserCertTypes.length > 0; else noUserCerts">
        <ng-container *ngFor="let certType of uploadedUserCertTypes; let last = last">

          <!-- Single-cert type (default) -->
          <ng-container *ngIf="!allowsMultiple(certType)">
            <div
              class="cert-item cert-uploaded p-3"
              [class.border-bottom]="!last"
            >
              <div class="d-flex justify-content-between align-items-start">
                <div class="d-flex align-items-start gap-3 flex-grow-1">
                  <div class="cert-icon flex-shrink-0">
                    <i class="bi bi-check-circle cert-icon-checked"></i>
                  </div>
                  <div class="flex-grow-1">
                    <div class="d-flex align-items-center gap-2 mb-1">
                      <h6 class="mb-0 fw-medium cert-title">{{ certType.label }}</h6>
                      <span *ngIf="certType.isRequired" class="badge badge-required small">Required</span>
                    </div>
                    <p class="small mb-0 cert-description text-muted">{{ certType.description }}</p>

                    <!-- Certification details -->
                    <div *ngIf="getCertificationForType(certType) as cert" class="mt-2 d-flex flex-wrap gap-3">
                      <div>
                        <span class="text-muted small">Certificate #:</span>
                        <span class="small fw-medium ms-1">{{ cert.certificateNumber }}</span>
                      </div>
                      <div *ngIf="cert.expirationDate">
                        <span class="text-muted small">Expires:</span>
                        <span class="small fw-medium ms-1">{{ formatDate(cert.expirationDate) }}</span>
                      </div>
                      <span class="badge" [ngClass]="getStatusBadgeClass(cert)">
                        {{ getStatusText(cert) }}
                      </span>
                    </div>
                  </div>
                </div>

                <div class="flex-shrink-0 ms-3">
                  <ng-container *ngIf="getCertificationForType(certType) as cert">
                    <div class="btn-group btn-group-sm">
                      <button class="btn btn-outline-secondary" (click)="editCertification(cert)">
                        <i class="bi bi-pencil"></i>
                      </button>
                      <button class="btn btn-outline-secondary" (click)="viewDocument(cert)" *ngIf="cert.documentUrl">
                        <i class="bi bi-file-earmark"></i>
                      </button>
                    </div>
                  </ng-container>
                </div>
              </div>
            </div>
          </ng-container>

          <!-- Multi-cert type (state-applicator) -->
          <ng-container *ngIf="allowsMultiple(certType)">
            <div
              *ngFor="let cert of getCertificationsForType(certType)"
              class="cert-item cert-uploaded p-3 border-bottom"
            >
              <div class="d-flex justify-content-between align-items-start">
                <div class="d-flex align-items-start gap-3 flex-grow-1">
                  <div class="cert-icon flex-shrink-0">
                    <i class="bi bi-check-circle cert-icon-checked"></i>
                  </div>
                  <div class="flex-grow-1">
                    <div class="d-flex align-items-center gap-2 mb-1">
                      <h6 class="mb-0 fw-medium cert-title">{{ certType.label }}{{ cert.state ? ' (' + cert.state + ')' : '' }}</h6>
                      <span *ngIf="certType.isRequired" class="badge badge-required small">Required</span>
                    </div>
                    <p class="small mb-0 cert-description text-muted">{{ certType.description }}</p>

                    <div class="mt-2 d-flex flex-wrap gap-3">
                      <div>
                        <span class="text-muted small">Certificate #:</span>
                        <span class="small fw-medium ms-1">{{ cert.certificateNumber }}</span>
                      </div>
                      <div *ngIf="cert.expirationDate">
                        <span class="text-muted small">Expires:</span>
                        <span class="small fw-medium ms-1">{{ formatDate(cert.expirationDate) }}</span>
                      </div>
                      <span class="badge" [ngClass]="getStatusBadgeClass(cert)">
                        {{ getStatusText(cert) }}
                      </span>
                    </div>
                  </div>
                </div>

                <div class="flex-shrink-0 ms-3">
                  <div class="btn-group btn-group-sm">
                    <button class="btn btn-outline-secondary" (click)="editCertification(cert)">
                      <i class="bi bi-pencil"></i>
                    </button>
                    <button class="btn btn-outline-secondary" (click)="viewDocument(cert)" *ngIf="cert.documentUrl">
                      <i class="bi bi-file-earmark"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Add for Another Cert Type -->
            <div class="cert-item p-3" [class.border-bottom]="!last">
              <div class="d-flex align-items-center gap-3 ps-1">
                <div class="cert-icon flex-shrink-0">
                  <i class="bi bi-plus-circle text-primary"></i>
                </div>
                <button class="btn btn-sm btn-outline-primary" (click)="addCertification(certType)">
                  <i class="bi bi-plus me-1"></i>{{ addCertificationText(certType) }}
                </button>
              </div>
            </div>
          </ng-container>

        </ng-container>
      </ng-container>
      <ng-template #noUserCerts>
        <div class="p-3 text-center text-muted">
          <i class="bi bi-folder2-open me-2"></i>
          No certifications uploaded yet
        </div>
      </ng-template>
    </div>
  </div>

  <!-- Missing Required Certifications -->
  <div class="card" [class.mb-3]="optionalUserCertTypes.length > 0" *ngIf="missingUserCertTypes.length > 0">
    <div class="card-header p-3">
      <h5 class="mb-0">Required Certifications</h5>
    </div>
    <div class="card-body p-0">
      <ng-container *ngFor="let certType of missingUserCertTypes; let last = last">
        <div
          class="cert-item cert-placeholder p-3"
          [class.border-bottom]="!last"
        >
          <div class="d-flex justify-content-between align-items-start">
            <div class="d-flex align-items-start gap-3 flex-grow-1">
              <div class="cert-icon flex-shrink-0">
                <i class="bi bi-circle text-muted"></i>
              </div>
              <div class="flex-grow-1">
                <div class="d-flex align-items-center gap-2 mb-1">
                  <h6 class="mb-0 fw-medium cert-title">{{ certType.label }}</h6>
                  <span class="badge badge-required small">Required</span>
                  <span class="badge badge-not-uploaded small">Not Uploaded</span>
                </div>
                <p class="small mb-0 cert-description">{{ certType.description }}</p>
              </div>
            </div>

            <div class="flex-shrink-0 ms-3">
              <button class="btn btn-sm btn-primary" (click)="addCertification(certType)">
                <i class="bi bi-plus me-1"></i>Add
              </button>
            </div>
          </div>
        </div>
      </ng-container>
    </div>
  </div>

  <!-- Optional Certifications -->
  <div class="card" *ngIf="optionalUserCertTypes.length > 0">
    <div class="card-header p-3">
      <h5 class="mb-0">Optional Certifications</h5>
    </div>
    <div class="card-body p-0">
      <ng-container *ngFor="let certType of optionalUserCertTypes; let last = last">
        <div
          class="cert-item cert-placeholder p-3"
          [class.border-bottom]="!last"
        >
          <div class="d-flex justify-content-between align-items-start">
            <div class="d-flex align-items-start gap-3 flex-grow-1">
              <div class="cert-icon flex-shrink-0">
                <i class="bi bi-circle text-muted"></i>
              </div>
              <div class="flex-grow-1">
                <div class="d-flex align-items-center gap-2 mb-1">
                  <h6 class="mb-0 fw-medium cert-title">{{ certType.label }}</h6>
                </div>
                <p class="small mb-0 cert-description">{{ certType.description }}</p>
              </div>
            </div>

            <div class="flex-shrink-0 ms-3">
              <button class="btn btn-sm btn-outline-primary" (click)="addCertification(certType)">
                <i class="bi bi-plus me-1"></i>Add
              </button>
            </div>
          </div>
        </div>
      </ng-container>
    </div>
  </div>
</div>

